/**
 * Utility functions for handling URL parameters in the booking flow
 */

/**
 * Sanitizes malformed URL search strings by fixing common issues
 */
const sanitizeUrlSearch = (search: string): string => {
  if (!search) return "";

  // Remove leading ? if present
  let sanitized = search.startsWith("?") ? search.slice(1) : search;

  // Fix double question marks (e.g., "products=123?locale=de" -> "products=123&locale=de")
  // Look for patterns like "param=value?param2=value2" and replace ? with &
  sanitized = sanitized.replace(/([^?&=]+=[^?&=]*)\?([^?&=]+=)/g, "$1&$2");

  // Fix multiple consecutive question marks
  sanitized = sanitized.replace(/\?+/g, "&");

  // Ensure we don't start with &
  sanitized = sanitized.replace(/^&+/, "");

  return sanitized;
};

/**
 * Get a URL parameter value by name with automatic URL sanitization
 */
export const getUrlParam = (paramName: string): string | null => {
  if (typeof window === "undefined") return null;

  try {
    // Always sanitize the URL search string first
    const sanitizedSearch = sanitizeUrlSearch(window.location.search);
    const sanitizedParams = new URLSearchParams(sanitizedSearch);

    return sanitizedParams.get(paramName);
  } catch (error) {
    console.error("Error parsing URL parameters:", error);
    return null;
  }
};

/**
 * Get multiple product IDs from the URL parameter
 */
export const getProductIdsFromUrl = (): string[] => {
  const productParam = getUrlParam("products");

  if (productParam) {
    try {
      return productParam.split(",").filter(Boolean);
    } catch (error) {
      console.error("Error parsing product IDs from URL:", error);
    }
  }

  return [];
};

/**
 * Safely call history.pushState/replaceState. WebKit throws
 * `SecurityError: The request was denied.` when the document is in a
 * sandboxed iframe (no `allow-same-origin`) or when Safari's pushState
 * rate limit (~100 calls / 30s) is exceeded. URL sync is best-effort —
 * the booking flow continues to work when this fails.
 */
export const safeUpdateHistory = (
  method: "pushState" | "replaceState",
  state: unknown,
  url: string,
): void => {
  if (typeof window === "undefined") return;
  // Skip no-op updates so we don't burn Safari's pushState rate-limit budget.
  const currentUrl = `${window.location.pathname}${window.location.search}`;
  if (url === currentUrl) return;
  try {
    window.history[method](state, "", url);
  } catch {
    /* sandboxed iframe / cross-origin / rate limit */
  }
};

/**
 * Update URL with booking parameters without causing a page reload
 */
export const updateUrlWithBookingParams = (params: Record<string, string>) => {
  if (typeof window === "undefined") return;

  const urlParams = new URLSearchParams(window.location.search);

  // Update or add each parameter
  Object.entries(params).forEach(([key, value]) => {
    if (value) {
      urlParams.set(key, value);
    } else {
      urlParams.delete(key);
    }
  });

  // Replace current URL without reload
  const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
  safeUpdateHistory("replaceState", { path: newUrl }, newUrl);
};

/**
 * Generate a URL for the booking flow with pre-selected products
 * @param productIds Array of product IDs to pre-select
 * @param baseUrl Base URL for the booking flow (optional)
 * @returns A URL string with the products parameter
 */
export const createBookingUrlWithProducts = (
  productIds: string[],
  baseUrl: string = "/",
): string => {
  if (!productIds || productIds.length === 0) {
    return baseUrl;
  }

  const productParam = productIds.join(",");
  const separator = baseUrl.includes("?") ? "&" : "?";

  return `${baseUrl}${separator}products=${encodeURIComponent(productParam)}`;
};

/**
 * Add DEXA scan to current URL for testing unavailable products
 */
export const addDexaScanToUrl = () => {
  if (typeof window === "undefined") return;

  const currentProducts = getProductIdsFromUrl();
  const dexaScanId = "44cdf268-5771-45bd-8b41-c6f2edbacdd9"; // DEXA scan internal UUID

  if (!currentProducts.includes(dexaScanId)) {
    const updatedProducts = [...currentProducts, dexaScanId];
    updateUrlWithBookingParams({
      products: updatedProducts.join(","),
    });

    // Reload to see the change
    window.location.reload();
  }
};

export interface PrefillUserInformation {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  birthday?: string;
  genderAtBirth?: "male" | "female";
  street?: string;
  streetNumber?: string;
  zipCode?: string;
  city?: string;
  country?: string;
  tosAndPpConsent?: boolean;
  medicalProcessing?: boolean;
  anonymousDataUsage?: boolean;
}

export interface TimeSlotParam {
  date: string; // YYYY-MM-DD format
  time: string; // HH:MM format
}

export interface TestModeParams {
  testUserId: string | null;
  userInformation: PrefillUserInformation | null;
  product: string | null;
  addOns: string[] | null;
  clinic: string | null;
  timeSlots: TimeSlotParam[] | null;
  jumpToLastStep: boolean;
  reportBackendDomain: string | null;
  testId: string | null;
}

export const getTestModeParams = (): TestModeParams => {
  const testUserId = getUrlParam("testUserId");
  const jumpToLastStep = getUrlParam("jumpToLastStep") === "true";
  const product = getUrlParam("product");
  const addOnsRaw = getUrlParam("addOns");
  const clinic = getUrlParam("clinic");
  const reportBackendDomain = getUrlParam("reportBackendDomain");
  const testId = getUrlParam("testId");

  let userInformation: PrefillUserInformation | null = null;
  const userInfoRaw = getUrlParam("userInformation");
  if (userInfoRaw) {
    try {
      userInformation = JSON.parse(decodeURIComponent(userInfoRaw));
    } catch (e) {
      console.error("[TestMode] Failed to parse userInformation:", e);
    }
  }

  let addOns: string[] | null = null;
  if (addOnsRaw) {
    addOns = addOnsRaw.split(",").filter(Boolean);
  }

  // Parse timeSlots - can be either a JSON array or a single date/time pair
  let timeSlots: TimeSlotParam[] | null = null;
  const timeSlotsRaw = getUrlParam("timeSlots");
  if (timeSlotsRaw) {
    try {
      const parsed = JSON.parse(decodeURIComponent(timeSlotsRaw));
      // Support both array and single object
      timeSlots = Array.isArray(parsed) ? parsed : [parsed];
    } catch (e) {
      console.error("[TestMode] Failed to parse timeSlots:", e);
    }
  }

  return {
    testUserId,
    userInformation,
    product,
    addOns,
    clinic,
    timeSlots,
    jumpToLastStep,
    reportBackendDomain,
    testId,
  };
};

export const isTestModeActive = (): boolean => {
  const params = getTestModeParams();
  return !!(
    params.testUserId ||
    params.userInformation ||
    params.product ||
    params.addOns ||
    params.clinic ||
    params.timeSlots ||
    params.jumpToLastStep ||
    params.reportBackendDomain
  );
};

// ============================================================================
// STEP URL MANAGEMENT (Browser History)
// ============================================================================

const VALID_STEPS = [
  "product",
  "cadence",
  "addons",
  "clinic",
  "date",
  "details",
  "confirmation",
] as const;
type ValidStep = (typeof VALID_STEPS)[number];

/**
 * Get the current step from URL parameter
 */
export const getStepFromUrl = (): ValidStep | null => {
  const step = getUrlParam("step");
  if (step && VALID_STEPS.includes(step as ValidStep)) {
    return step as ValidStep;
  }
  return null;
};

/**
 * Update URL with step parameter using pushState (adds to browser history)
 */
export const pushStepToHistory = (step: string) => {
  if (typeof window === "undefined") return;

  const urlParams = new URLSearchParams(window.location.search);
  urlParams.set("step", step);

  const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
  safeUpdateHistory("pushState", { step }, newUrl);
};

/**
 * Replace current URL with step parameter (no new history entry)
 */
export const replaceStepInUrl = (step: string) => {
  if (typeof window === "undefined") return;

  const urlParams = new URLSearchParams(window.location.search);
  urlParams.set("step", step);

  const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
  safeUpdateHistory("replaceState", { step }, newUrl);
};
